import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CreditPack } from '@claude-code-api/shared';

interface BuyCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canPurchase: boolean;
  reason?: string | null;
  returnPath: string;
}

export function BuyCreditsDialog({
  open,
  onOpenChange,
  canPurchase,
  reason,
  returnPath,
}: BuyCreditsDialogProps) {
  const { t } = useTranslation('usage');
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [redirectingPackId, setRedirectingPackId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!open) return;

    const loadPacks = async () => {
      setLoading(true);
      try {
        const packsRes = await fetch('/api/credits/packs', {
          credentials: 'include',
        });
        if (!packsRes.ok) return;
        const payload = await packsRes.json();
        setPacks(payload.data?.packs || []);
      } finally {
        setLoading(false);
      }
    };

    void loadPacks();
  }, [open]);

  const baseUnitPrice = useMemo(() => {
    if (packs.length === 0) return 0;
    const sorted = packs
      .slice()
      .sort((a, b) => Number(a.creditAmount) - Number(b.creditAmount));
    return (
      Number(sorted[0].priceUsd) / Math.max(1, Number(sorted[0].creditAmount))
    );
  }, [packs]);

  const startCheckout = async (packId: string) => {
    setRedirectingPackId(packId);
    try {
      const res = await fetch('/api/credits/packs/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packId, returnPath }),
      });
      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error || t('dialog.messages.startCheckoutFailed'));
        return;
      }
      if (result.data?.applied) {
        toast.success(
          result.data.creditsAdded
            ? t('dialog.messages.creditsAddedCount', {
                count: Number(result.data.creditsAdded),
              })
            : t('dialog.messages.creditsAdded'),
        );
        onOpenChange(false);
        return;
      }
      if (!result.data?.checkoutUrl) {
        toast.error(t('dialog.messages.checkoutUrlMissing'));
        return;
      }
      window.location.href = String(result.data.checkoutUrl);
    } catch {
      toast.error(t('dialog.messages.startCheckoutFailed'));
    } finally {
      setRedirectingPackId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
        </DialogHeader>

        {!canPurchase && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {reason || t('dialog.activePlanRequired')}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">
            {t('dialog.loadingPlans')}
          </p>
        ) : packs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dialog.noPacks')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packs.map((pack) => {
              const unitPrice =
                Number(pack.priceUsd) / Math.max(1, Number(pack.creditAmount));
              const rawDiscountPct =
                baseUnitPrice > 0 ? (1 - unitPrice / baseUnitPrice) * 100 : 0;
              const discountPct = Math.max(
                0,
                Math.min(99, Math.floor(rawDiscountPct)),
              );
              const highlight = Number(pack.creditAmount) >= 2000;

              return (
                <div
                  key={pack.id}
                  className={`relative border rounded-lg p-6 text-center space-y-4 transition-shadow hover:shadow-md ${
                    highlight ? 'border-primary ring-1 ring-primary/20' : ''
                  }`}
                >
                  {highlight && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-semibold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                      {discountPct > 0
                        ? t('dialog.savePercent', { percent: discountPct })
                        : t('dialog.bestValue')}
                    </span>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{pack.name}</h3>
                    <p className="text-3xl font-bold mt-1">
                      {Number(pack.creditAmount).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('dialog.creditsUnit')}
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      ${Number(pack.priceUsd).toFixed(0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('dialog.unitPrice', { price: unitPrice.toFixed(4) })}
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    variant={highlight ? 'default' : 'outline'}
                    disabled={!canPurchase || redirectingPackId === pack.id}
                    onClick={() => {
                      void startCheckout(pack.id);
                    }}
                  >
                    {redirectingPackId === pack.id
                      ? t('dialog.redirecting')
                      : t('buyCredits')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
